import React, {useState} from 'react';
import {Button, Checkbox} from 'library/components';
import {useLang, useForm} from 'library/hooks';
import styled from 'styled-components';

// Use internal theme instead of global
const theme = {

    backgroundColor: { 

        buttons: {

            consent: '#7eb058', // Green
            options: 'grey'
        }
    },

    color: {

        icon: '#7eb058', // Green
        link: '#3a3a3a' // Dark gray
    }
};

const Link = styled.a(props => `

    font-size: 1em;

    &:link, &:visited, &active {

        color: ${theme.color.link};
    }

    &:hover {

        text-decoration: none;
    }
`);

const Modal = styled.div(props => `

    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${props.theme.padding.medium};
    background: rgba(0, 0, 0, 0.75);
`);

const ModalContent = styled.div(props => `

    width: 100%;
    max-width: 800px;
    padding: ${props.theme.padding.medium};
    padding-bottom: 0;
    border-radius: ${props.theme.borderRadius.small};
    background: ${props.theme.backgroundColor.core.secondary};
    border: 1px solid ${props.theme.borderColor.core.primary};
`);

const Buttons = styled.div(props => `

    display: flex;

    & > button:first-of-type {

        margin-right: ${props.theme.margin.small};
    }

    & > button:last-of-type {

        margin-left: ${props.theme.margin.small};
    }
`);

const Title = styled.div(props => `

    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: ${props.theme.margin.medium};
`);

const Cookie = styled.i(props => `

    color: ${props.theme.color.positive.primary};
    margin-right: ${props.theme.margin.small};
`);

const Info = styled.div(props => `

    padding-bottom: ${props.theme.padding.medium};
`);

const Toggle = styled.div(props => `

    display: flex;
    position: fixed;
    bottom: 0;
    right: ${props.theme.padding.small};
    padding: ${props.theme.padding.small};
    background: ${props.theme.backgroundColor.core.secondary};
    border-radius: ${props.theme.borderRadius.small} ${props.theme.borderRadius.small} 0 0;
    border: 1px solid ${props.theme.borderColor.core.primary};
    cursor: pointer;
`);

const ToggleText = styled.div(props => `

    font-size: 0.75em;
    padding-right: ${props.theme.padding.small};
    user-select: none;
`);

const ToggleIcon = styled.i(props => `

    color: ${theme.color.icon};
`);

const ExternalInfo = styled.div(props => `

    padding-bottom: ${props.theme.padding.small};
`);

const Indent = styled.div(props => `

    padding-left: ${props.theme.padding.medium};
`);

// Special case which uses internal translations instead of lang file
const internal_translations = {

    fi: {

        "title": "Evästeiden käyttö",
        "cookies": "Evästeet",
        "about_cookies": "Käytämme evästeitä palvelun mainontaan liittyen. Voit vaihtaa näitä mahdollisimman yksinkertaisia asetuksia milloin vain palvelun oikeasta alakulmasta myös sen jälkeen, kun olet hyväksynyt evästeet.",
        "consent_all": "Hyväksy evästeet",
        "consent_selected": "Tallenna valinnat",
        "settings" :"Lisäasetukset",
        "google_ad_storage": "Google: Mahdollistaa mainontaan liittyvän tallennuksen.",
        "google_ad_user_data": "Google: Asettaa suostumuksen käyttäjätietojen lähettämiselle mainontaan liittyen.",
        "microsoft_ad_storage": "Microsoft: Mahdollistaa mainontaan liittyvän tallennuksen."
    }
}

export default function ConsentModal(props){

    const {lang} = useLang();
    const translations = internal_translations[lang];
    const consent_settings = JSON.parse(window.localStorage.getItem('consent_settings'));

    const [state, setState] = useState({

        isOpen: consent_settings === null ? true : false,
        settings: false
    });

    const form = new Map();

    // Use existing values
    if(consent_settings !== null){
        
        if(props.google && consent_settings.google !== undefined){
        
            form.set('google_ad_user_data', consent_settings.google.ad_user_data === 'granted' ? true : false);
            form.set('google_ad_storage', consent_settings.google.ad_storage === 'granted' ? true : false);
        }

        if(props.microsoft && consent_settings.microsoft !== undefined){

            form.set('microsoft_ad_storage', consent_settings.microsoft.ad_storage === 'granted' ? true : false);
        }
    }

    // Set defaults
    else {
        
        if(props.google){

            form.set('google_ad_storage', true);
            form.set('google_ad_user_data', true);
        }

        if(props.microsoft){

            form.set('microsoft_ad_storage', true);
        }
    }

    const {getForm, setForm} = useForm(form);

    function consentAll(){

        const options = {}

        if(props.google){

            setForm({name: 'google_ad_user_data', value: true});
            setForm({name: 'google_ad_storage', value: true});

            options.google = {

                ad_storage: 'granted',
                ad_user_data: 'granted'
            }
        }

        if(props.microsoft){

            setForm({name: 'microsoft_ad_storage', value: true});

            options.microsoft = {

                ad_storage: 'granted'
            }
        }

        consent(options);
    }

    function consentSelected(){

        const options = {};

        if(props.google){

            options.google = {

                ad_storage: getForm('google_ad_storage') === true ? 'granted' : 'denied',
                ad_user_data: getForm('google_ad_user_data') === true ? 'granted' : 'denied'
            }
        }

        if(props.microsoft){

            options.microsoft = {

                ad_storage: getForm('microsoft_ad_storage') === true ? 'granted' : 'denied'
            }
        }

        consent(options);
    }

    function consent(options){

        if(Object.keys(options).length !== 0){

            window.localStorage.setItem('consent_settings', JSON.stringify(options));

            if(props.google){

                // Update Google consent
                window.gtag('consent', 'update', options.google);
            }

            if(props.microsoft){

                // Update Microsoft consent
                window.uetq.push('consent', 'update', options.microsoft);
            }
        }

        setState({isOpen: false, settings: false});
    }

    function openConsent(){

        setState(previous_state => ({

            ...previous_state,
            isOpen: true
        }));
    }

    function toggleSettings(){

        setState(previous_state => ({

            ...previous_state,
            settings: ! previous_state.settings
        }));
    }

    return (

        <React.Fragment>

            {state.isOpen === true &&

                <React.Fragment>

                    <Modal>

                        <ModalContent>

                            <Title><Cookie className="fas fa-cookie fa-2x" />{translations.title}</Title>

                            <Info>{translations.about_cookies}</Info>

                            {state.settings === true &&

                                <Indent>

                                    {props.google &&
                                        
                                        <React.Fragment>

                                            <ExternalInfo>

                                                <p>
                                                Lue <Link href="https://business.safety.google/intl/fi/privacy/" target="_blank">tästä</Link> lisää siitä, miten <b>Google</b> käsittelee henkilökohtaisia tietoja.
                                                </p>

                                            </ExternalInfo>

                                            <Indent>

                                                <Checkbox label={translations.google_ad_storage} name="google_ad_storage" value={true} checked={getForm('google_ad_storage')} onChange={setForm} styled={{marginBottom: '1em'}} />
                                                <Checkbox label={translations.google_ad_user_data} name="google_ad_user_data" value={true} checked={getForm('google_ad_user_data')} onChange={setForm} styled={{marginBottom: '1em'}} />

                                            </Indent>

                                        </React.Fragment>
                                    }

                                    {props.microsoft &&

                                        <React.Fragment>

                                            <ExternalInfo>

                                                <p>
                                                Lue <Link href="https://www.microsoft.com/fi-fi/privacy/privacystatement" target="_blank">tästä</Link> lisää siitä, miten <b>Microsoft</b> käsittelee henkilökohtaisia tietoja.
                                                </p>

                                            </ExternalInfo>

                                            <Indent>

                                                <Checkbox label={translations.microsoft_ad_storage} name="microsoft_ad_storage" value={true} checked={getForm('microsoft_ad_storage')} onChange={setForm} styled={{marginBottom: '1em'}} />

                                            </Indent>

                                        </React.Fragment>
                                    }

                                </Indent>
                            }

                            <Buttons>

                                {state.settings === false && <Button onClick={consentAll} style={{background: theme.backgroundColor.buttons.consent, border: 0, height: 'auto', padding: '1.5em 0 1.5em 0'}}>{translations.consent_all}</Button>}
                                {state.settings === true && <Button onClick={consentSelected} style={{background: theme.backgroundColor.buttons.consent, border: 0, height: 'auto', padding: '1.5em 0 1.5em 0'}}>{translations.consent_selected}</Button>} 
                                <Button onClick={toggleSettings} style={{background: theme.backgroundColor.buttons.options, border: 0, height: 'auto', padding: '1.5em 0 1.5em 0'}}>{translations.settings}</Button>

                            </Buttons>

                        </ModalContent>

                    </Modal>

                </React.Fragment>
            }

            {state.isOpen === false &&

                <Toggle onClick={openConsent}>

                    <ToggleText>{translations.cookies}</ToggleText>
                    <ToggleIcon className="fas fa-cookie"></ToggleIcon>

                </Toggle>
            }

        </React.Fragment>
    );
}
